<template>
	<modal :show.sync="showNameConfirmationModal" effect="custom" class="this-is-the-class">
		<div slot="modal-header" class="modal-header center">
			<h4 class="modal-title">Welcome to UNOapp!</h4>
		</div>
		<div slot="modal-body" class="modal-body">
			<h4 v-if="activeUser.first_name">Can we call you <b>{{ activeUser.first_name }}</b>? Confirm your name below if that's right.</h4>
			<h4 v-else>We didn't catch your name. Let us know what we should call you.</h4>
			<form novalidate>
				<div class="alert alert-danger" v-if="errorMessage.length">
				    <button class="close" data-close="alert" @click="clearError()"></button>
				    <span>{{errorMessage}}</span>
				</div>
				<div class="form-body">
	                <div class="form-group form-md-line-input form-md-floating-label">
                        <input type="text" class="form-control" id="form_control_1" maxlength="20" v-model="activeUser.first_name" :class="{'edited': activeUser.first_name}">
                        <label for="form_control_1">First Name</label>
                    </div>
                    <div class="form-group form-md-line-input form-md-floating-label">
                        <input type="text" class="form-control" id="form_control_2" maxlength="20" v-model="activeUser.last_name" :class="{'edited': activeUser.last_name}">
                        <label for="form_control_2">Last Name</label>
                    </div>
	            </div>
	        </form>
		</div>
		<div slot="modal-footer" class="modal-footer">
			<button type="button" class="btn btn-primary" @click="showAlert($event)">Confirm</button>
		</div>
	</modal>
</template>

<script>
import Modal from './modules/Modal'
import User from '../controllers/User'
import AppVue from '../components/App'
import Auth from '../controllers/Auth'
import Account from '../controllers/Account'
import { mapActions, mapGetters } from 'vuex'

export default {
	data () {
		return {
			errorMessage: '',
			showNameConfirmationModal: false,
			activeUser: {}
		}
	},
	mounted () {
		if (this.activeUserName) {
			this.activeUser = Object.assign({}, this.activeUserName)
			this.showNameConfirmationModal = true
		} else {
			AppVue.logOut()
		}
	},
	computed: mapGetters([
		'activeUserName',
		'userAccountsLength',
		'firstUserAccount',
		'selectedAccountId'
	]),
	methods: {
		/**
		 * To check if the first and last name are valid before submitting to the backend.
		 * @function
		 * @returns {object} A promise that will validate the input form
		 */
		validateUserData () {
			let _this = this
			return new Promise((resolve, reject) => {
				if (!_this.activeUser.first_name.length) {
					reject('Your first name cannot be blank')
				} else if (!_this.activeUser.last_name.length) {
					reject('Your last name cannot be blank')
				}
				resolve('Hurray')
			})
		},
		/**
		 * To alert the user that they won't be able to change their name after this.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {undefined}
		 */
		showAlert (event) {
			let _this = this
			return _this.validateUserData()
			.then(response => {
				_this.errorMessage = ''
				this.$swal({
					title: 'Are you sure?',
					text: 'You will not be able to change your name after this!',
					type: 'warning',
					showCancelButton: true,
					confirmButtonText: 'Yes, confirm name!',
					cancelButtonText: 'No, change name',
					allowEnterKey: false
				}).then(() => {
					_this.confirmUserName(event)
				}, dismiss => {
					// Do nothing on dismiss
				})
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
			})
		},
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To confirm the name of the user that logged in for the first time and determine if any action needs to take place.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {undefined}
		 */
		confirmUserName (event) {
			let _this = this
			User.confirmUserName(_this.activeUser).then(response => {
				this.updateActiveUserName(_this.activeUser)
				// if the user is currently logged into an account then just redirect to dashboard
				if (_this.selectedAccountId) {
					_this.$router.push('dashboard')
				} else {
					if (_this.userAccountsLength > 1) {
						_this.$router.push('select_account')
					} else if (_this.userAccountsLength === 1) {
						// Since this is the only account, just select the single account returned
						_this.loginToSelectedAccount(_this.firstUserAccount.account_id)
					}
				}
			}).catch(_this.$root.errorWrapper(e => {
				// If the confirm name attempt fails then display the error message
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'already_confirmed':
						this.updateActiveUserName(_this.activeUser)
						// if the user is currently logged into an account then just redirect to dashboard
						if (_this.selectedAccountId) {
							_this.$router.push('dashboard')
						} else {
							if (_this.userAccountsLength > 1) {
								_this.$router.push('select_account')
							} else if (_this.userAccountsLength === 1) {
								// Since this is the only account, just select the single account returned
								_this.loginToSelectedAccount(_this.firstUserAccount.account_id)
							}
						}
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To login to the selected user account
		 * @function
		 * @param {integer} accountId - The account id that we want to login to
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 */
		loginToSelectedAccount (accountId) {
			let _this = this

			Auth.loginToAccount(accountId).then(response => {
				_this.getSelectedAccountDetails()
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'invalid_account':
						_this.routeMessage = 'We were unable to find the account you selected. Please contact the admin.'
						_this.$router.message = _this.routeMessage
						_this.$router.push({path: '/error', props: {message: _this.routeMessage}})
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To get the details of the selected user account and send/emit these details to the parent.
		 * @function
		 * @returns {object} - A promise that will either return an error message or perform an action.
		 */
		getSelectedAccountDetails () {
			let _this = this

			Account.getCurrentAccountDetails().then(response => {
				_this.setActiveAccount(response.payload)
				_this.setActiveBusinessName(response.payload.business_name)
				_this.$router.push('dashboard')
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'account_not_found':
						_this.routeMessage = 'We were unable to find the account you selected. Please contact the admin.'
						_this.$router.message = _this.routeMessage
						_this.$router.push({path: '/error', props: {message: _this.routeMessage}})
						break

					default:
						// No default
					}
				}
			}))
		},
		...mapActions([
			'updateActiveUserName',
			'setActiveAccount',
			'setActiveBusinessName'
		])
	},
	components: {
		Modal
	}
}
</script>

<style>
</style>


// WEBPACK FOOTER //
// ConfirmName.vue?87af14e6